import { z } from 'zod';
import { EstimateLineKindSchema, EstimateNoteKindSchema, EstimateSectionKindSchema, EstimateStatusSchema, EstimateVersionStatusSchema, } from '../lib/enums';
import { ESTIMATE_NUMBER_REGEX } from '../lib/constants';
import { MarkupSchema, TaxSchema, zEmail, zIsoDateTime, zMoney, zMoneyNonNeg, zPct, zQty, zRate, zSha256Hex, zUuid, } from './common';
/**
 * Estimates. Product laws enforced by these shapes:
 * - line items are the source of truth; every total is DERIVED by the shared
 *   engine (server authoritative) — computed values live in `computed`, never
 *   as writable fields;
 * - markup/margin always carries an explicit `mode`;
 * - sent estimates are immutable; revisions create versions;
 * - snapshot-on-use: a line's `unitCost`/`costDetail` are frozen copies made
 *   at insert; `sourceItemId` is provenance only and never re-read for math.
 */
export const EstimateNumberSchema = z.string().regex(ESTIMATE_NUMBER_REGEX);
// --------------------------------------------------------------------- header
export const EstimateSchema = z.object({
    id: zUuid,
    number: EstimateNumberSchema,
    title: z.string().min(1).max(300),
    status: EstimateStatusSchema,
    clientId: zUuid,
    projectSiteId: zUuid.nullable(),
    tradeProfileKey: z.string().min(1).max(64),
    currentVersionId: zUuid.nullable(),
    isPublicWork: z.boolean(),
    /** Prevailing-wage set applied when set (labor lines only). */
    wageRateSetId: zUuid.nullable(),
    tax: TaxSchema.nullable(),
    /** Estimate-level markup layer (explicit mode, always). */
    markup: MarkupSchema,
    /**
     * Materials markup. Applies to `kind: 'material'` lines INSTEAD of `markup`
     * (inheritance, never stacking), and only where the line and its section
     * carry no override. Null means material lines use `markup`, which is how
     * every estimate written before this field behaved.
     *
     * Resolved once at creation from the client's then the company's default,
     * so a sent estimate stays self-contained; frozen on send by the same
     * draft-lock trigger that freezes `markup`.
     *
     * `.default(null)` is load-bearing, not laziness. Frozen version snapshots
     * (`estimate_versions.snapshot`) are immutable legal artifacts written before
     * this field existed, and they are re-parsed through this schema to verify
     * their stored hash. They have no `materialsMarkup` key and must never be
     * rewritten to gain one — so its absence has to parse, and it means exactly
     * what it should: that estimate had no materials markup.
     */
    materialsMarkup: MarkupSchema.nullable().default(null),
    /** Intake answers captured at creation from the trade profile's intakeFields. */
    intake: z.record(z.union([z.string(), z.number(), z.boolean()])).nullable(),
    createdAt: zIsoDateTime,
    updatedAt: zIsoDateTime,
    sentAt: zIsoDateTime.nullable(),
});
export const EstimateSummarySchema = z.object({
    id: zUuid,
    number: EstimateNumberSchema,
    title: z.string(),
    status: EstimateStatusSchema,
    clientId: zUuid,
    clientName: z.string().nullable(),
    tradeProfileKey: z.string(),
    /** Base price + tax of the latest computation; null for never-computed drafts. */
    total: zMoney.nullable(),
    currentVersionId: zUuid.nullable(),
    createdAt: zIsoDateTime,
    updatedAt: zIsoDateTime,
    sentAt: zIsoDateTime.nullable(),
});
// ------------------------------------------------------------------- sections
export const EstimateSectionSchema = z.object({
    id: zUuid,
    position: z.number().int().min(0),
    title: z.string().min(1).max(300),
    kind: EstimateSectionKindSchema,
    /** Alternates in the same group are mutually exclusive choices. */
    alternateGroup: z.string().max(64).nullable(),
    /** Set on kind='sub_rollup' sections created by quote acceptance. */
    sourceScopePackageId: zUuid.nullable(),
    markup: MarkupSchema.nullable(),
});
export const CreateSectionRequestSchema = z
    .object({
    title: z.string().min(1).max(300),
    kind: EstimateSectionKindSchema.exclude(['sub_rollup']).optional(),
    position: z.number().int().min(0).optional(),
    alternateGroup: z.string().max(64).nullable().optional(),
    markup: MarkupSchema.nullable().optional(),
})
    .strict();
export const UpdateSectionRequestSchema = CreateSectionRequestSchema.partial();
export const ReorderSectionsRequestSchema = z
    .object({
    /** Every section id of the estimate, in the desired order. */
    sectionIds: z.array(zUuid).min(1),
})
    .strict();
// ---------------------------------------------------------------------- lines
/** Provenance of a snapshotted line (never re-read for math). */
export const LineSourceTypeSchema = z.enum([
    'labor_role',
    'crew',
    'material',
    'equipment_rate',
    'assembly',
    'sub_quote',
]);
/**
 * Frozen cost-resolution detail captured at snapshot time. Objects are
 * passthrough: the engine may record extra detail; parsing must not strip it
 * (these blobs participate in content hashing).
 */
export const CostDetailSchema = z.union([
    z
        .object({
        kind: z.literal('labor'),
        baseRate: zRate,
        burden: z.array(z
            .object({
            label: z.string(),
            kind: z.enum(['percent', 'per_hour']),
            value: z.number(),
            perHourAmount: zRate.optional(),
        })
            .passthrough()),
        burdenedRate: zRate,
        /** Present when a prevailing-wage set swapped base+fringe. */
        prevailing: z
            .object({
            wageRateSetId: zUuid,
            classification: z.string(),
            baseRate: zRate,
            fringeRate: zRate,
        })
            .passthrough()
            .nullable()
            .optional(),
    })
        .passthrough(),
    z
        .object({
        kind: z.literal('assembly'),
        components: z.array(z
            .object({
            kind: z.enum(['labor', 'material', 'equipment']),
            description: z.string(),
            hoursPerUnit: zQty.optional(),
            qtyPerUnit: zQty.optional(),
            wastePct: z.number().optional(),
            unitCost: zRate.nullable(),
        })
            .passthrough()),
        unitCost: zRate,
    })
        .passthrough(),
    z.object({ kind: z.literal('material') }).passthrough(),
    z.object({ kind: z.literal('equipment') }).passthrough(),
    z
        .object({
        kind: z.literal('subquote'),
        subQuoteId: zUuid,
        /** The sub's quoted PRICE (passes through the engine untouched at cost layer). */
        quotedPrice: zMoney,
    })
        .passthrough(),
]);
export const EstimateLineSchema = z.object({
    id: zUuid,
    sectionId: zUuid,
    position: z.number().int().min(0),
    kind: EstimateLineKindSchema,
    description: z.string().max(1000),
    qty: zQty,
    unit: z.string().max(20).nullable(),
    /** Snapshot unit COST (null = unpriced; engine treats as 0 and flags it). */
    unitCost: zRate.nullable(),
    costDetail: CostDetailSchema.nullable(),
    /** Line-level markup override (explicit mode); null = inherit section/estimate. */
    markup: MarkupSchema.nullable(),
    taxable: z.boolean(),
    sourceItemType: LineSourceTypeSchema.nullable(),
    sourceItemId: zUuid.nullable(),
    subQuoteId: zUuid.nullable(),
});
// -------------------------------------------------------------- lines:batch
/**
 * Write shape for a line. On create, either provide `unitCost` directly
 * (custom pricing) or provide `sourceItemType`+`sourceItemId` and let the
 * server resolve + snapshot the cost from the library (snapshot-on-use).
 */
export const LineWriteSchema = z
    .object({
    /** Client-generated correlation id for creates; echoed back in `idMap`. */
    tempId: z.string().min(1).max(64).optional(),
    /** Present for updates. Exactly one of id/tempId is required. */
    id: zUuid.optional(),
    sectionId: zUuid,
    position: z.number().int().min(0).optional(),
    kind: EstimateLineKindSchema,
    description: z.string().max(1000),
    qty: zQty.optional(),
    unit: z.string().max(20).nullable().optional(),
    unitCost: zRate.nullable().optional(),
    markup: MarkupSchema.nullable().optional(),
    taxable: z.boolean().optional(),
    sourceItemType: LineSourceTypeSchema.nullable().optional(),
    sourceItemId: zUuid.nullable().optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    if ((v.id === undefined) === (v.tempId === undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'exactly one of id (update) or tempId (create) is required',
        });
    }
    if ((v.sourceItemType === undefined) !== (v.sourceItemId === undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'sourceItemType and sourceItemId must be provided together',
        });
    }
});
export const LinesBatchOpSchema = z.discriminatedUnion('op', [
    z.object({ op: z.literal('upsert'), line: LineWriteSchema }).strict(),
    z.object({ op: z.literal('delete'), id: zUuid }).strict(),
    z
        .object({
        op: z.literal('move'),
        id: zUuid,
        toSectionId: zUuid,
        position: z.number().int().min(0),
    })
        .strict(),
]);
/** `PUT /estimates/:id/lines:batch` — ordered ops, applied atomically. */
export const LinesBatchRequestSchema = z
    .object({
    ops: z.array(LinesBatchOpSchema).min(1).max(500),
})
    .strict();
// ---------------------------------------------------------------------- notes
export const EstimateNoteSchema = z.object({
    id: zUuid,
    kind: EstimateNoteKindSchema,
    body: z.string().min(1).max(4000),
    position: z.number().int().min(0),
});
export const CreateNoteRequestSchema = z
    .object({
    kind: EstimateNoteKindSchema,
    body: z.string().min(1).max(4000),
    position: z.number().int().min(0).optional(),
})
    .strict();
export const UpdateNoteRequestSchema = CreateNoteRequestSchema.partial();
// ------------------------------------------------------------------- computed
/**
 * Engine output layer totals. Both effective figures are ALWAYS present and
 * labeled — showing one without the other is a product-law violation.
 * They are null only when cost is zero (percentages undefined).
 */
export const LayerTotalsSchema = z.object({
    cost: zMoney,
    price: zMoney,
    effectiveMarkupPct: zPct.nullable(),
    effectiveMarginPct: zPct.nullable(),
});
export const ComputedLineSchema = LayerTotalsSchema.extend({
    lineId: zUuid,
    taxable: z.boolean(),
    /** True when unitCost was null (line not yet priced — surfaced in UI). */
    unpriced: z.boolean(),
});
export const ComputedSectionSchema = LayerTotalsSchema.extend({
    sectionId: zUuid,
    kind: EstimateSectionKindSchema,
    /** Alternates are excluded from base totals; allowances included + flagged. */
    includedInBase: z.boolean(),
});
export const ComputedEstimateSchema = z.object({
    lines: z.array(ComputedLineSchema),
    sections: z.array(ComputedSectionSchema),
    /** Base scope: standard + allowance + sub_rollup sections. */
    base: LayerTotalsSchema,
    /** Allowance subtotal (already included in base, flagged separately). */
    allowances: z.object({ cost: zMoney, price: zMoney }),
    /** Each alternate section priced separately; NOT in base. */
    alternates: z.array(z.object({
        sectionId: zUuid,
        alternateGroup: z.string().nullable(),
        cost: zMoney,
        price: zMoney,
    })),
    tax: z.object({
        label: z.string(),
        ratePct: zPct,
        taxableBase: zMoney,
        amount: zMoneyNonNeg,
    }),
    /** base.price (before tax). */
    subtotal: zMoney,
    /** subtotal + tax.amount. Invariant: totals = Σ(rounded components). */
    total: zMoney,
});
// -------------------------------------------------------------- estimate CRUD
export const CreateEstimateRequestSchema = z
    .object({
    clientId: zUuid,
    projectSiteId: zUuid.nullable().optional(),
    tradeProfileKey: z.string().min(1).max(64),
    title: z.string().min(1).max(300),
    isPublicWork: z.boolean().optional(),
    wageRateSetId: zUuid.nullable().optional(),
    templateId: zUuid.optional(),
    intake: z.record(z.union([z.string(), z.number(), z.boolean()])).optional(),
})
    .strict();
export const UpdateEstimateRequestSchema = z
    .object({
    title: z.string().min(1).max(300).optional(),
    clientId: zUuid.optional(),
    projectSiteId: zUuid.nullable().optional(),
    isPublicWork: z.boolean().optional(),
    wageRateSetId: zUuid.nullable().optional(),
    tax: TaxSchema.nullable().optional(),
    markup: MarkupSchema.optional(),
    /** Explicit null clears it, so material lines fall back to `markup`. */
    materialsMarkup: MarkupSchema.nullable().optional(),
    intake: z.record(z.union([z.string(), z.number(), z.boolean()])).nullable().optional(),
})
    .strict();
/** Full draft + server-computed totals — the GET /estimates/:id payload. */
export const EstimateDetailResponseSchema = z.object({
    estimate: EstimateSchema,
    sections: z.array(EstimateSectionSchema),
    lines: z.array(EstimateLineSchema),
    notes: z.array(EstimateNoteSchema),
    computed: ComputedEstimateSchema,
});
export const LinesBatchResponseSchema = z.object({
    /** tempId → created line id. */
    idMap: z.record(zUuid),
    computed: ComputedEstimateSchema,
});
export const EstimateListQuerySchema = z.object({
    status: EstimateStatusSchema.optional(),
    clientId: zUuid.optional(),
    q: z.string().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
// ------------------------------------------------------------------- versions
export const EstimateVersionSchema = z.object({
    id: zUuid,
    estimateId: zUuid,
    versionNumber: z.number().int().min(1),
    status: EstimateVersionStatusSchema,
    contentHash: zSha256Hex,
    sentAt: zIsoDateTime.nullable(),
    viewedAt: zIsoDateTime.nullable(),
    frozenAt: zIsoDateTime,
});
/**
 * The frozen canonical computed document stored in `estimate_versions.snapshot`.
 * `content_hash = canonicalJsonHash(snapshot)`. Passthrough at top level so
 * re-parsing a stored snapshot never alters bytes that were hashed.
 */
export const EstimateVersionSnapshotSchema = z
    .object({
    schemaVersion: z.literal(1),
    estimate: EstimateSchema.omit({ status: true, currentVersionId: true }),
    sections: z.array(EstimateSectionSchema),
    lines: z.array(EstimateLineSchema),
    notes: z.array(EstimateNoteSchema),
    computed: ComputedEstimateSchema,
    generatedAt: zIsoDateTime,
})
    .passthrough();
export const EstimateVersionDetailSchema = EstimateVersionSchema.extend({
    snapshot: EstimateVersionSnapshotSchema,
});
export const EstimateVersionsResponseSchema = z.array(EstimateVersionSchema);
// ---------------------------------------------------------------- send/revise
export const SendEstimateRequestSchema = z
    .object({
    recipient: z
        .object({
        name: z.string().max(200).optional(),
        email: zEmail,
    })
        .strict(),
    /** Personal message included in the outbox email. */
    message: z.string().max(5000).optional(),
    /** Proposal token expiry; server default applies when omitted. */
    expiresAt: zIsoDateTime.optional(),
})
    .strict();
export const SendEstimateResponseSchema = z.object({
    estimate: EstimateSchema,
    version: EstimateVersionSchema,
    /** Tokenized public link (also delivered via the outbox email). */
    publicUrl: z.string().url(),
});
/** Revise: current version → superseded, estimate back to draft. */
export const ReviseEstimateResponseSchema = z.object({
    estimate: EstimateSchema,
    supersededVersionId: zUuid,
});
export const DuplicateEstimateResponseSchema = EstimateDetailResponseSchema;
// ------------------------------------------------------------------ templates
const TemplateLineSchema = z
    .object({
    kind: EstimateLineKindSchema,
    description: z.string().max(1000),
    qty: zQty.nullable(),
    unit: z.string().max(20).nullable(),
    /** Company's own values may be carried; never seeded market prices. */
    unitCost: zRate.nullable(),
    markup: MarkupSchema.nullable(),
    taxable: z.boolean(),
})
    .strict();
export const EstimateTemplateContentSchema = z
    .object({
    sections: z.array(z
        .object({
        title: z.string().min(1).max(300),
        kind: EstimateSectionKindSchema.exclude(['sub_rollup']),
        alternateGroup: z.string().max(64).nullable(),
        markup: MarkupSchema.nullable(),
        lines: z.array(TemplateLineSchema),
    })
        .strict()),
    notes: z.array(z
        .object({
        kind: EstimateNoteKindSchema,
        body: z.string().min(1).max(4000),
    })
        .strict()),
})
    .strict();
export const EstimateTemplateSchema = z.object({
    id: zUuid,
    name: z.string().min(1).max(200),
    description: z.string().max(1000).nullable(),
    tradeProfileKey: z.string().max(64).nullable(),
    content: EstimateTemplateContentSchema,
    createdAt: zIsoDateTime,
    updatedAt: zIsoDateTime,
});
export const CreateEstimateTemplateRequestSchema = z
    .object({
    name: z.string().min(1).max(200),
    description: z.string().max(1000).nullable().optional(),
    tradeProfileKey: z.string().max(64).nullable().optional(),
    /** Provide content directly, or derive it from an existing estimate. */
    content: EstimateTemplateContentSchema.optional(),
    fromEstimateId: zUuid.optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    if ((v.content === undefined) === (v.fromEstimateId === undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'provide exactly one of content or fromEstimateId',
        });
    }
});
export const UpdateEstimateTemplateRequestSchema = z
    .object({
    name: z.string().min(1).max(200).optional(),
    description: z.string().max(1000).nullable().optional(),
    tradeProfileKey: z.string().max(64).nullable().optional(),
    content: EstimateTemplateContentSchema.optional(),
})
    .strict();
//# sourceMappingURL=estimates.js.map